import { TableInner } from '@chocolate-soup-inc/cs-frontend-components';
import { useEffect, useMemo, useState } from 'react';
import { Filters, TFiltersProps } from '../../components/filters/Filters';
import { TablePage } from '../../components/tablePage/TablePage';
import { useFilter } from '../../contexts/filters';
import { useQueryListAllCompaniesOptions } from '../../entities/companies/queries';
import { useEventReport } from '../../entities/reports/queries';
import { TCustomReportEntity } from '../../generated/graphql';
import { ReportActions } from './ReportActions';
import { companyFilter, eventReportEndDate, monthFilter } from './reportFilters';

import './ReportPage.scss';

type TRow = {
  companyId: string;
  companyName: string;
  counts: Record<string, number>;
  total: number;
  isTotal?: boolean;
};

export const EventsReport = () => {
  const { filtersPageMode } = useFilter();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [companyId, setCompanyId] = useState<string>();
  const { data: rows, error: reportError, loading: reportLoading } = useEventReport(month, companyId);
  const { data: companies, error: companiesError, loading: companiesLoading } = useQueryListAllCompaniesOptions();

  useEffect(() => {
    filtersPageMode();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const eventTypes = useMemo(() => [...new Set(rows.map((row) => row.eventType))].sort(), [rows]);
  const companyRows = useMemo(() => {
    const companies = new Map<string, TRow>();
    const total: TRow = { companyId: '', companyName: 'TOTAL', counts: Object.create(null), total: 0, isTotal: true };

    for (const row of rows) {
      const company = companies.get(row.companyId) || { companyId: row.companyId, companyName: row.companyName, counts: Object.create(null), total: 0 };
      company.counts[row.eventType] = (company.counts[row.eventType] || 0) + row.count;
      company.total += row.count;
      companies.set(row.companyId, company);
      total.counts[row.eventType] = (total.counts[row.eventType] || 0) + row.count;
      total.total += row.count;
    }

    return companies.size ? [...companies.values(), total] : [];
  }, [rows]);
  const sheet = useMemo(
    () => [['Month', month], ['Company ID', 'Company', ...eventTypes, 'TOTAL'], ...companyRows.map((row) => [row.companyId, row.companyName, ...eventTypes.map((type) => row.counts[type] || 0), row.total])],
    [month, eventTypes, companyRows]
  );
  const loading = reportLoading || companiesLoading;
  const error = reportError || companiesError;

  const filters = useMemo<TFiltersProps['filters']>(() => [monthFilter(month, setMonth, eventReportEndDate().slice(0, 7)), companyFilter(companies, companyId, setCompanyId)], [month, companies, companyId]);

  return (
    <TablePage dataLoading={loading} error={error} title='Events (monthly)'>
      <Filters filters={filters} />
      <div style={{ overflow: 'auto' }}>
        <TableInner<TRow>
          enableSorting={true}
          defaultSorting={{ id: 'companyName', desc: false }}
          columns={[
            {
              accessorKey: 'companyId',
              // Undefined sort values keep the total last in either sort direction.
              accessorFn: (row) => (row.isTotal ? undefined : row.companyId),
              sortUndefined: 'first',
              cell: ({ row }) => (
                <div className='uuid-label' title={row.original.companyId}>
                  {row.original.companyId}
                </div>
              ),
              header: 'Company ID'
            },
            {
              accessorKey: 'companyName',
              accessorFn: (row) => (row.isTotal ? undefined : row.companyName),
              sortUndefined: 'first',
              cell: ({ row }) => row.original.companyName,
              header: 'Company'
            },
            ...eventTypes.map((type) => ({
              id: `event:${type}`,
              accessorFn: (row: TRow) => (row.isTotal ? undefined : row.counts[type] || 0),
              sortUndefined: 'first' as const,
              cell: ({ row }: { row: { original: TRow } }) => row.original.counts[type] || 0,
              header: type
            })),
            {
              accessorKey: 'total',
              accessorFn: (row) => (row.isTotal ? undefined : row.total),
              sortUndefined: 'first',
              cell: ({ row }) => row.original.total,
              header: 'TOTAL'
            }
          ]}
          data={companyRows}
          emptyText='No events were found.'
          getRowId={(row) => (row.isTotal ? 'total' : row.companyId)}
          expandable={false}
          fillSelectedRows={false}
          fixedHeader={true}
          hideSubRowsSelect={true}
          hoverableRows={true}
          selectableRows={false}
          virtual={false}
        />
      </div>
      <ReportActions data={sheet} disabled={loading || !!error || rows.length === 0} filename={`cs-events-report_${month}`} customReportEntity={TCustomReportEntity.Events} />
    </TablePage>
  );
};
